import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, CircleNotch, Receipt, BracketsCurly } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ALLOWED_TEMPLATE_VARIABLES,
  TEMPLATE_KIND_LABELS,
  createBillTemplate,
  updateBillTemplate,
  type BillSettingsOwnerType,
  type BillTemplate,
  type BillTemplateKind,
} from '@/lib/billSettingsApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  initial?: BillTemplate;
  defaultKind?: BillTemplateKind;
  // Per docs/14: page-level scope (mine vs chambers) decides who owns the
  // new template. Edit mode ignores these — ownership is immutable post-create.
  forceOwnerType?: BillSettingsOwnerType;
  forceBarristerOwnerId?: string | null;
  onClose: () => void;
  onSaved: (t: BillTemplate) => void;
}

export function BillTemplateModal({
  initial,
  defaultKind = 'reminder',
  forceOwnerType,
  forceBarristerOwnerId,
  onClose,
  onSaved,
}: Props) {
  const qc = useQueryClient();
  const isEdit = Boolean(initial);

  const [name, setName] = useState(initial?.name ?? '');
  const [kind] = useState<BillTemplateKind>(initial?.kind ?? defaultKind);
  const [subject, setSubject] = useState(initial?.subject ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  const subjectRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [lastFocus, setLastFocus] = useState<'subject' | 'body'>('body');

  const insertVariable = (v: string) => {
    const token = `{${v}}`;
    if (lastFocus === 'subject' && subjectRef.current) {
      const el = subjectRef.current;
      const start = el.selectionStart ?? subject.length;
      const end = el.selectionEnd ?? subject.length;
      const next = subject.slice(0, start) + token + subject.slice(end);
      setSubject(next);
      // Restore caret after the inserted token.
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      });
    } else if (bodyRef.current) {
      const el = bodyRef.current;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = body.slice(0, start) + token + body.slice(end);
      setBody(next);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      });
    }
  };

  const saveMut = useMutation({
    mutationFn: async (): Promise<BillTemplate> => {
      const trimmedName = name.trim();
      if (!trimmedName) throw new Error('Name is required');
      if (!subject.trim()) throw new Error('Subject is required');
      if (!body.trim()) throw new Error('Body is required');

      if (isEdit && initial) {
        return updateBillTemplate(initial.id, {
          name: trimmedName,
          subject: subject.trim(),
          body: body.trim(),
          isDefault,
          isActive,
        });
      }
      return createBillTemplate({
        name: trimmedName,
        kind,
        subject: subject.trim(),
        body: body.trim(),
        isDefault,
        ownerType: forceOwnerType ?? 'chambers',
        barristerOwnerId:
          (forceOwnerType ?? 'chambers') === 'barrister' ? forceBarristerOwnerId ?? null : null,
      });
    },
    onSuccess: (t) => {
      toast.success(isEdit ? 'Template updated' : 'Template created');
      qc.invalidateQueries({ queryKey: ['bill-templates'] });
      onSaved(t);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not save template')),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saveMut.isPending) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex max-h-full w-full max-w-2xl flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <Receipt size={15} weight="duotone" className="text-brand-600 dark:text-brand-400" />
              {isEdit ? 'Edit template' : 'New template'}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {TEMPLATE_KIND_LABELS[kind]}
              {!isEdit && forceOwnerType === 'barrister' && ' · saved to your personal templates'}
              {!isEdit && forceOwnerType === 'chambers' && ' · saved to chambers shared templates'}
              {isEdit && ' · type cannot be changed after creation'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !saveMut.isPending && onClose()}
            disabled={saveMut.isPending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Template name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "First reminder — friendly"'
              maxLength={100}
              disabled={saveMut.isPending}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Subject
            </label>
            <Input
              ref={subjectRef}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => setLastFocus('subject')}
              placeholder="Reminder: invoice {bill_number} due in {grace_period}"
              maxLength={300}
              disabled={saveMut.isPending}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Body
            </label>
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onFocus={() => setLastFocus('body')}
              placeholder={'Dear {bill_recipient_first_name},\n\nThis is a friendly reminder…'}
              rows={10}
              maxLength={20000}
              disabled={saveMut.isPending}
              className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-[12.5px] leading-relaxed shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-600"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Click a variable below to insert it at the cursor in {lastFocus === 'subject' ? 'the subject' : 'the body'}.
            </p>
          </div>

          {/* Variable picker */}
          <div className="rounded-md border border-border bg-muted/30 p-2">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              <BracketsCurly size={11} weight="duotone" /> Autofill variables
            </div>
            <div className="flex flex-wrap gap-1">
              {ALLOWED_TEMPLATE_VARIABLES.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertVariable(v)}
                  disabled={saveMut.isPending}
                  className="rounded border border-border bg-card px-1.5 py-0.5 text-xs hover:bg-brand-50 hover:text-brand-700 dark:hover:text-brand-300"
                >
                  {`{${v}}`}
                </button>
              ))}
            </div>
          </div>

          {/* Toggles */}
          <div className="grid gap-2 md:grid-cols-2">
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                disabled={saveMut.isPending}
                className="h-3.5 w-3.5 cursor-pointer accent-brand-600"
              />
              <span className="font-medium">Default for this kind</span>
              <span className="text-muted-foreground">— pre-selected on send</span>
            </label>
            {isEdit && (
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={saveMut.isPending}
                  className="h-3.5 w-3.5 cursor-pointer accent-emerald-600"
                />
                <span className="font-medium">Active</span>
                <span className="text-muted-foreground">— available to choose</span>
              </label>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saveMut.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
            className="gap-1.5"
          >
            {saveMut.isPending && <CircleNotch size={14} className="animate-spin" />}
            {isEdit ? 'Save changes' : 'Create template'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
